import { useState, useRef, useEffect, useCallback, type FC } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  Chip,
  Link,
  Alert,
  Fade,
  Tooltip,
  LinearProgress
} from '@mui/material';
import { styled, alpha, keyframes } from '@mui/material/styles';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ChevronRightIcon from '@untitled-ui/icons-react/build/esm/ChevronRight';
import Settings02Icon from '@untitled-ui/icons-react/build/esm/Settings02';
import Lightning01Icon from '@untitled-ui/icons-react/build/esm/Lightning01';
import CheckCircleIcon from '@untitled-ui/icons-react/build/esm/CheckCircle';
import ArrowRightIcon from '@untitled-ui/icons-react/build/esm/ArrowRight';
import MicrophoneIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import {
  sendAgentMessage,
  getStoredApiKey,
  setStoredApiKey,
  hasEnvApiKey,
  type Message as AgentMessage
} from './agent-service';
import { executeTool } from './agent-tools';
import { useVoiceRecorder } from './useVoiceRecorder';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  toolsUsed?: string[];
  isLoading?: boolean;
}

interface AgentDialogProps {
  open: boolean;
  onClose: () => void;
}

// Animations
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const micPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4); }
  50% { box-shadow: 0 0 0 10px rgba(239, 68, 68, 0); }
`;

// Quick spin animation for interactions
const robotSpin = keyframes`
  0% { transform: rotateY(0deg) scale(1); }
  25% { transform: rotateY(180deg) scale(1.1); }
  50% { transform: rotateY(360deg) scale(1); }
  75% { transform: rotateY(540deg) scale(1.1); }
  100% { transform: rotateY(720deg) scale(1); }
`;

// Slow continuous spin - one rotation every 20 seconds with 3D effect
const slowSpin = keyframes`
  0% {
    transform: rotateY(0deg) rotateX(0deg);
    filter: drop-shadow(0 0 8px rgba(41, 112, 255, 0.3));
  }
  25% {
    transform: rotateY(90deg) rotateX(5deg);
    filter: drop-shadow(0 0 15px rgba(16, 185, 129, 0.5));
  }
  50% {
    transform: rotateY(180deg) rotateX(0deg);
    filter: drop-shadow(0 0 20px rgba(139, 92, 246, 0.6));
  }
  75% {
    transform: rotateY(270deg) rotateX(-5deg);
    filter: drop-shadow(0 0 15px rgba(16, 185, 129, 0.5));
  }
  100% {
    transform: rotateY(360deg) rotateX(0deg);
    filter: drop-shadow(0 0 8px rgba(41, 112, 255, 0.3));
  }
`;

const StyledDrawer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  right: 0,
  height: '100vh',
  width: 520,
  maxWidth: '100vw',
  backgroundColor: theme.palette.mode === 'dark' ? '#0a0a0b' : '#ffffff',
  borderLeft: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
  boxShadow: theme.palette.mode === 'dark'
    ? '-8px 0 40px rgba(0, 0, 0, 0.5)'
    : '-8px 0 40px rgba(0, 0, 0, 0.1)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  zIndex: 1300,
  transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  [theme.breakpoints.down('sm')]: {
    width: '100vw',
    borderLeft: 'none'
  }
}));

const MessageBubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isUser'
})<{ isUser?: boolean }>(({ theme, isUser }) => ({
  padding: '14px 18px',
  borderRadius: isUser ? '18px 18px 6px 18px' : '18px 18px 18px 6px',
  backgroundColor: isUser
    ? 'linear-gradient(135deg, #2970FF 0%, #1e5fd9 100%)'
    : theme.palette.mode === 'dark'
      ? alpha('#ffffff', 0.04)
      : alpha('#000000', 0.03),
  background: isUser
    ? 'linear-gradient(135deg, #2970FF 0%, #1e5fd9 100%)'
    : undefined,
  color: isUser
    ? '#ffffff'
    : theme.palette.mode === 'dark'
      ? 'rgba(255,255,255,0.9)'
      : 'rgba(0,0,0,0.85)',
  maxWidth: '88%',
  wordBreak: 'break-word',
  whiteSpace: 'pre-wrap',
  fontSize: '0.9rem',
  lineHeight: 1.6,
  boxShadow: isUser
    ? '0 4px 14px rgba(41, 112, 255, 0.25)'
    : 'none'
}));

const ToolChip = styled(Chip)(({ theme }) => ({
  height: 22,
  fontSize: '0.7rem',
  fontWeight: 500,
  backgroundColor: theme.palette.mode === 'dark'
    ? alpha('#10B981', 0.15)
    : alpha('#10B981', 0.1),
  color: '#10B981',
  border: `1px solid ${alpha('#10B981', 0.2)}`,
  '& .MuiChip-icon': {
    color: '#10B981',
    marginLeft: 6
  }
}));

const ThinkingDots = styled(Box)(() => ({
  display: 'flex',
  gap: 4,
  padding: '4px 0',
  '& span': {
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: '#2970FF',
    animation: `${pulse} 1.4s ease-in-out infinite`,
    '&:nth-of-type(2)': {
      animationDelay: '0.2s'
    },
    '&:nth-of-type(3)': {
      animationDelay: '0.4s'
    }
  }
}));

const QuickAction = styled(Button)(({ theme }) => ({
  borderRadius: 20,
  padding: '6px 14px',
  fontSize: '0.75rem',
  fontWeight: 500,
  textTransform: 'none',
  backgroundColor: theme.palette.mode === 'dark'
    ? 'rgba(255,255,255,0.06)'
    : 'rgba(0,0,0,0.04)',
  color: theme.palette.mode === 'dark'
    ? 'rgba(255,255,255,0.85)'
    : 'rgba(0,0,0,0.75)',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  minHeight: 32,
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(41,112,255,0.15)'
      : 'rgba(41,112,255,0.08)',
    borderColor: '#2970FF',
    color: '#2970FF',
    transform: 'translateY(-1px)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 4px 12px rgba(41,112,255,0.2)'
      : '0 4px 12px rgba(41,112,255,0.15)',
  }
}));

const ConversationAction = styled(Button)(({ theme }) => ({
  borderRadius: 20,
  padding: '6px 14px',
  fontSize: '0.75rem',
  fontWeight: 500,
  textTransform: 'none',
  backgroundColor: theme.palette.mode === 'dark'
    ? 'rgba(139,92,246,0.1)'
    : 'rgba(139,92,246,0.06)',
  color: theme.palette.mode === 'dark'
    ? 'rgba(255,255,255,0.85)'
    : 'rgba(0,0,0,0.75)',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(139,92,246,0.25)' : 'rgba(139,92,246,0.2)'}`,
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  minHeight: 32,
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(139,92,246,0.2)'
      : 'rgba(139,92,246,0.12)',
    borderColor: '#8B5CF6',
    color: '#8B5CF6',
    transform: 'translateY(-1px)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 4px 12px rgba(139,92,246,0.25)'
      : '0 4px 12px rgba(139,92,246,0.15)',
  }
}));

const ActionLinkButton = styled(Button)(({ theme }) => ({
  borderRadius: 10,
  padding: '8px 16px',
  fontSize: '0.8rem',
  fontWeight: 600,
  textTransform: 'none',
  backgroundColor: '#2970FF',
  color: '#fff',
  marginTop: 12,
  boxShadow: '0 2px 8px rgba(41, 112, 255, 0.3)',
  '&:hover': {
    backgroundColor: '#1e5fd9',
    boxShadow: '0 4px 12px rgba(41, 112, 255, 0.4)'
  }
}));

// ChatGPT Logo Icon Component
const ChatGPTIcon: FC<{
  size?: number;
  animate?: boolean;
  inverted?: boolean;
  transparent?: boolean;
  white?: boolean;
  continuousSpin?: boolean; // Slow 20-second rotation
}> = ({ size = 40, animate = false, inverted = false, transparent = false, white = false, continuousSpin = false }) => (
  <Box
    sx={{
      width: size,
      height: size,
      flexShrink: 0,
      borderRadius: '50%',
      backgroundColor: transparent ? 'transparent' : (inverted ? '#000000' : '#ffffff'),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'visible', // Allow glow effects to show
      perspective: '1000px', // Enable 3D perspective for cooler rotation
      animation: animate
        ? `${robotSpin} 1s cubic-bezier(0.4, 0, 0.2, 1) forwards`
        : continuousSpin
          ? `${slowSpin} 20s ease-in-out infinite`
          : 'none',
      transformStyle: 'preserve-3d',
    }}
  >
    <Box
      component="img"
      src="/chatgpt.png"
      alt="ChatGPT"
      sx={{
        width: size,
        height: size,
        filter: white ? 'brightness(0) invert(1)' : (transparent ? 'none' : (inverted ? 'brightness(0) invert(1)' : 'brightness(0)')),
        objectFit: 'cover',
        transition: 'filter 0.3s ease',
      }}
    />
  </Box>
);

// Parse markdown-style links [text](url) and return structured content
function parseMessageContent(content: string): { text: string; links: Array<{ label: string; url: string }> } {
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  const links: Array<{ label: string; url: string }> = [];
  let match;

  while ((match = linkRegex.exec(content)) !== null) {
    links.push({ label: match[1], url: match[2] });
  }

  // Remove links from text
  const text = content.replace(linkRegex, '').trim();

  return { text, links };
}

// Split text into sentences for text-message style bubbles
function splitIntoSentences(text: string): string[] {
  if (!text) return [];

  // Split on sentence-ending punctuation followed by space or end of string
  // But preserve the punctuation with the sentence
  const sentences = text
    .split(/(?<=[.!?])\s+/)
    .map(s => s.trim())
    .filter(s => s.length > 0);

  return sentences;
}

export const AgentDialog: FC<AgentDialogProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [rateLimitError, setRateLimitError] = useState<string | null>(null);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [robotAnimating, setRobotAnimating] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const cooldownRef = useRef<NodeJS.Timeout | null>(null);
  const prevOpenRef = useRef(false);

  const usingEnvKey = hasEnvApiKey();

  // Voice recorder hook - works in Brave and all browsers
  const {
    isRecording,
    isProcessing,
    startRecording,
    stopRecording,
    isSupported: isVoiceSupported,
    audioLevel
  } = useVoiceRecorder({
    onTranscript: (text) => {
      setInput(prev => prev ? `${prev} ${text}` : text);
      setVoiceError(null);
    },
    onError: (error) => {
      setVoiceError(error);
      setTimeout(() => setVoiceError(null), 5000);
    }
  });

  // Toggle voice recording
  const toggleRecording = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, stopRecording, startRecording]);

  // Cooldown timer effect
  useEffect(() => {
    if (cooldownSeconds > 0) {
      cooldownRef.current = setTimeout(() => {
        setCooldownSeconds(prev => prev - 1);
        if (cooldownSeconds === 1) {
          setRateLimitError(null);
        }
      }, 1000);
    }
    return () => {
      if (cooldownRef.current) clearTimeout(cooldownRef.current);
    };
  }, [cooldownSeconds]);

  const handleLinkClick = (url: string) => {
    onClose();
    navigate(url);
  };

  useEffect(() => {
    const storedKey = getStoredApiKey();
    if (storedKey) {
      setApiKey(storedKey);
    }
  }, []);

  // Trigger robot spin animation when modal opens
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setRobotAnimating(true);
      const timer = setTimeout(() => {
        setRobotAnimating(false);
      }, 1000); // Animation duration
      return () => clearTimeout(timer);
    }
    prevOpenRef.current = open;
  }, [open]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // No welcome message - start with empty messages

  const handleSaveApiKey = () => {
    setStoredApiKey(apiKeyInput);
    setApiKey(apiKeyInput);
    setShowSettings(false);
  };

  const handleQuickAction = (prompt: string) => {
    setInput(prompt);
  };

  // Direct quarterly analytics PDF generation (bypasses AI, runs tool directly)
  const handleGenerateQuarterlyAnalytics = async () => {
    setIsGeneratingReport(true);
    try {
      const result = await executeTool('quarterly_analytics', { quarter: 'current' });
      if (result.success && result.data) {
        const data = result.data as { message: string; summary: { totalRevenue: string; netProfit: string; profitMargin: string; projectsCompleted: number; totalFootage: string } };
        // Add success message to chat
        const successMessage: Message = {
          id: Date.now().toString(),
          role: 'assistant',
          content: `${data.message}\n\nQuarter Summary:\n• Revenue: ${data.summary.totalRevenue}\n• Net Profit: ${data.summary.netProfit}\n• Profit Margin: ${data.summary.profitMargin}\n• Projects: ${data.summary.projectsCompleted}\n• Footage: ${data.summary.totalFootage}`,
          timestamp: new Date(),
          toolsUsed: ['quarterly_analytics']
        };
        setMessages(prev => [...prev, successMessage]);
      } else {
        const errorMessage: Message = {
          id: Date.now().toString(),
          role: 'assistant',
          content: `Failed to generate quarterly analytics: ${result.error || 'Unknown error'}`,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      const errorMessage: Message = {
        id: Date.now().toString(),
        role: 'assistant',
        content: `Error generating report: ${error instanceof Error ? error.message : 'Unknown error'}`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading || cooldownSeconds > 0) return;

    const effectiveApiKey = apiKey || getStoredApiKey();
    if (!effectiveApiKey) {
      setShowSettings(true);
      return;
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setRateLimitError(null);

    const loadingId = (Date.now() + 1).toString();
    setMessages(prev => [
      ...prev,
      {
        id: loadingId,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        isLoading: true
      }
    ]);

    try {
      const apiMessages: AgentMessage[] = messages
        .filter(m => m.id !== 'welcome' && !m.isLoading)
        .map(m => ({
          role: m.role,
          content: m.content
        }));

      apiMessages.push({
        role: 'user',
        content: userMessage.content
      });

      const response = await sendAgentMessage(apiMessages, effectiveApiKey);

      // Check for rate limit errors and set cooldown
      if (response.error) {
        const errorLower = response.error.toLowerCase();
        if (errorLower.includes('rate limit') || errorLower.includes('quota') || errorLower.includes('exceeded')) {
          // Extract wait time from error if present (default to 30s, not 60s)
          const waitMatch = response.error.match(/(\d+\.?\d*)\s*s/i);
          const waitSeconds = waitMatch ? Math.ceil(parseFloat(waitMatch[1])) + 3 : 30;
          setCooldownSeconds(waitSeconds);
          setRateLimitError(`Rate limit reached. Please wait ${waitSeconds}s before trying again.`);

          setMessages(prev =>
            prev.map(m =>
              m.id === loadingId
                ? {
                    ...m,
                    content: `Rate limit reached. Please wait ${waitSeconds} seconds and try again. Your message has been saved.`,
                    isLoading: false
                  }
                : m
            )
          );
          // Restore the input so user doesn't lose their message
          setInput(userMessage.content);
          return;
        }
      }

      const responseContent = response.error || response.message;
      setMessages(prev =>
        prev.map(m =>
          m.id === loadingId
            ? {
                ...m,
                content: responseContent,
                isLoading: false,
                toolsUsed: response.toolsUsed
              }
            : m
        )
      );

    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      const errorLower = errorMsg.toLowerCase();

      // Check if it's a rate limit error
      if (errorLower.includes('rate limit') || errorLower.includes('quota') || errorLower.includes('exceeded')) {
        const waitMatch = errorMsg.match(/(\d+\.?\d*)\s*s/i);
        const waitSeconds = waitMatch ? Math.ceil(parseFloat(waitMatch[1])) + 3 : 30;
        setCooldownSeconds(waitSeconds);
        setRateLimitError(`Rate limit reached. Please wait ${waitSeconds}s before trying again.`);
        setInput(userMessage.content);
      }

      setMessages(prev =>
        prev.map(m =>
          m.id === loadingId
            ? {
                ...m,
                content: 'Sorry, something went wrong. Please try again.',
                isLoading: false
              }
            : m
        )
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && cooldownSeconds === 0) {
      e.preventDefault();
      handleSend();
    }
  };

  const showQuickActions = messages.length === 0;

  if (!open) return null;

  return (
    <>
      {/* Backdrop overlay */}
      <Fade in={open}
timeout={300}>
        <Box
          onClick={onClose}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            backdropFilter: 'blur(4px)',
            zIndex: 1299,
            cursor: 'pointer'
          }}
        />
      </Fade>

      {/* Side Panel */}
      <StyledDrawer
        sx={{
          transform: open ? 'translateX(0)' : 'translateX(100%)'
        }}
      >
        {/* Header - Premium Design */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            py: 2,
            borderBottom: (theme) =>
              `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}`,
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? 'linear-gradient(180deg, rgba(41,112,255,0.02) 0%, transparent 100%)'
                : 'linear-gradient(180deg, rgba(41,112,255,0.015) 0%, transparent 100%)'
          }}
        >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ minWidth: 0, overflow: 'hidden' }}
        >
          <Box
            sx={{
              p: 0.75,
              borderRadius: '12px',
              flexShrink: 0,
              background: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, rgba(41,112,255,0.12) 0%, rgba(41,112,255,0.04) 100%)'
                  : 'linear-gradient(135deg, rgba(41,112,255,0.08) 0%, rgba(41,112,255,0.02) 100%)',
              border: (theme) =>
                `1px solid ${theme.palette.mode === 'dark' ? 'rgba(41,112,255,0.15)' : 'rgba(41,112,255,0.1)'}`,
            }}
          >
            <ChatGPTIcon size={32} continuousSpin />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: '1rem',
                fontWeight: 700,
                color: (theme) =>
                  theme.palette.mode === 'dark' ? '#fff' : '#0a0a0b',
                letterSpacing: '-0.02em'
              }}
            >
              ChatGPT
            </Typography>
            <Stack direction="row"
spacing={0.75}
alignItems="center">
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: '#10B981',
                  boxShadow: '0 0 8px rgba(16,185,129,0.6)'
                }}
              />
              <Typography
                sx={{
                  fontSize: '0.65rem',
                  color: (theme) => theme.palette.mode === 'dark' ? 'rgba(16,185,129,0.9)' : '#10B981',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}
              >
                Online
              </Typography>
            </Stack>
          </Box>
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            flexShrink: 0,
            ml: 'auto',
            pl: 2,
          }}
        >
          {!usingEnvKey && (
            <IconButton
              size="small"
              onClick={() => setShowSettings(true)}
              sx={{
                color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)',
                borderRadius: '10px',
                minWidth: 32,
                minHeight: 32,
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                  color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.7)'
                }
              }}
            >
              <Settings02Icon style={{ width: 18, height: 18 }} />
            </IconButton>
          )}
          <Tooltip title="Start over">
            <IconButton
              size="small"
              onClick={() => {
                setMessages([]);
                setInput('');
              }}
              sx={{
                color: '#2970FF',
                borderRadius: '10px',
                width: 36,
                height: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(41, 112, 255, 0.12)',
                  color: '#1d5fd9',
                  transform: 'rotate(90deg)',
                }
              }}
            >
              <RefreshRoundedIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton
              size="small"
              onClick={onClose}
              sx={{
                color: '#ef4444',
                borderRadius: '10px',
                width: 36,
                height: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(239, 68, 68, 0.12)',
                  color: '#dc2626',
                  transform: 'scale(1.05)',
                }
              }}
            >
              <CloseRoundedIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Settings Panel */}
      {showSettings && (
        <Box
          sx={{
            p: 2.5,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: (theme) =>
              theme.palette.mode === 'dark' ? alpha('#ffffff', 0.02) : alpha('#000000', 0.01)
          }}
        >
          <Typography variant="subtitle2"
sx={{ mb: 1.5, fontWeight: 600 }}>
            API Configuration
          </Typography>
          <Alert
            severity="info"
            sx={{
              mb: 2,
              fontSize: '0.8rem',
              borderRadius: 2,
              '& .MuiAlert-icon': { fontSize: 18 }
            }}
          >
            Get your API key at{' '}
            <Link href="https://platform.openai.com/api-keys"
target="_blank"
rel="noopener">
              platform.openai.com
            </Link>
          </Alert>
          <TextField
            fullWidth
            size="small"
            type="password"
            placeholder="Enter your OpenAI API key"
            value={apiKeyInput}
            onChange={(e) => setApiKeyInput(e.target.value)}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              }
            }}
          />
          <Stack direction="row"
spacing={1}
justifyContent="flex-end">
            <Button
              size="small"
              onClick={() => setShowSettings(false)}
              sx={{ borderRadius: 2 }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleSaveApiKey}
              disabled={!apiKeyInput.trim()}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                boxShadow: 'none'
              }}
            >
              Save Key
            </Button>
          </Stack>
        </Box>
      )}

      {/* Quick Actions & Conversation Mode - Combined Premium Design */}
      {showQuickActions && (
        <Box
          sx={{
            px: 2.5,
            py: 2.5,
            borderBottom: (theme) =>
              `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
          }}
        >
          {/* Quick Actions */}
          <Box sx={{ mb: 2.5 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: '#2970FF',
                }}
              />
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                Quick Actions
              </Typography>
            </Stack>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.75, flexWrap: 'wrap' }}>
              <QuickAction
                startIcon={<Lightning01Icon style={{ width: 12, height: 12 }} />}
                onClick={() => handleQuickAction('Create a quote for ')}
              >
                New Quote
              </QuickAction>
              <QuickAction
                onClick={() => handleQuickAction('Search for customer ')}
              >
                Search Customer
              </QuickAction>
              <QuickAction
                onClick={() => handleQuickAction('Show recent leads')}
              >
                Recent Leads
              </QuickAction>
              <QuickAction
                onClick={() => handleQuickAction('Download a backup of all leads')}
              >
                Backup Leads
              </QuickAction>
              <QuickAction
                onClick={() => handleQuickAction('Create a new invoice for ')}
              >
                New Invoice
              </QuickAction>
              <QuickAction
                onClick={() => handleQuickAction('Create a new agreement for ')}
              >
                New Agreement
              </QuickAction>
              <QuickAction
                onClick={() => handleQuickAction('Create a text message to ')}
              >
                Send Text
              </QuickAction>
              <QuickAction
                onClick={() => handleQuickAction('Send a quote email to ')}
              >
                Send Email
              </QuickAction>
            </Box>
          </Box>

          {/* Conversation Mode */}
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: '#8B5CF6',
                }}
              />
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                Conversation Mode
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.65rem',
                  fontWeight: 500,
                  color: (theme) => theme.palette.mode === 'dark' ? 'rgba(139,92,246,0.6)' : 'rgba(139,92,246,0.7)',
                  ml: 0.5,
                }}
              >
                (Guided)
              </Typography>
            </Stack>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.75, flexWrap: 'wrap' }}>
              <ConversationAction
                onClick={() => handleQuickAction('I want to create a quote')}
              >
                Create a Quote
              </ConversationAction>
              <ConversationAction
                onClick={() => handleQuickAction('I want to send an email')}
              >
                Send an Email
              </ConversationAction>
              <ConversationAction
                onClick={() => handleQuickAction('I want to create a new lead')}
              >
                Create a Lead
              </ConversationAction>
              <ConversationAction
                onClick={() => handleQuickAction('I want to update a customer')}
              >
                Update Customer
              </ConversationAction>
              <ConversationAction
                onClick={handleGenerateQuarterlyAnalytics}
                disabled={isGeneratingReport}
                sx={{
                  background: (theme) => theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(41,112,255,0.15) 100%)'
                    : 'linear-gradient(135deg, rgba(16,185,129,0.1) 0%, rgba(41,112,255,0.1) 100%)',
                  borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(16,185,129,0.3)' : 'rgba(16,185,129,0.25)',
                  '&:hover': {
                    background: (theme) => theme.palette.mode === 'dark'
                      ? 'linear-gradient(135deg, rgba(16,185,129,0.25) 0%, rgba(41,112,255,0.25) 100%)'
                      : 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(41,112,255,0.15) 100%)',
                    borderColor: '#10B981',
                    color: '#10B981',
                  }
                }}
              >
                Get Quarterly Analytics
              </ConversationAction>
            </Box>
          </Box>
        </Box>
      )}

      {/* Messages */}
      <Box
        sx={{
          p: 2.5,
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          '&::-webkit-scrollbar': {
            width: 6
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.15)',
            borderRadius: 3
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent'
          }
        }}
      >
        <Stack spacing={2.5}
sx={{ flex: 1 }}>
          {messages.map((message) => {
            // For assistant messages, split into sentences for text-message style
            const isAssistant = message.role === 'assistant';
            const parsedContent = parseMessageContent(message.content);
            const sentences = isAssistant && !message.isLoading
              ? splitIntoSentences(parsedContent.text)
              : [parsedContent.text];

            return (
              <Fade in
key={message.id}
timeout={300}>
                <Stack
                  direction="row"
                  justifyContent={message.role === 'user' ? 'flex-end' : 'flex-start'}
                  spacing={1.5}
                >
                  {message.role === 'assistant' && (
                    <Box sx={{ mt: 0.5 }}>
                      <ChatGPTIcon size={34} animate={robotAnimating && message.id === 'welcome'} />
                    </Box>
                  )}
                  <Box sx={{ maxWidth: '85%' }}>
                    {message.isLoading ? (
                      <MessageBubble isUser={false}>
                        <Stack direction="row"
spacing={1.5}
alignItems="center">
                          <ThinkingDots>
                            <span />
                            <span />
                            <span />
                          </ThinkingDots>
                          <Typography
                            variant="body2"
                            sx={{ color: 'text.secondary', fontSize: '0.85rem' }}
                          >
                            Processing...
                          </Typography>
                        </Stack>
                      </MessageBubble>
                    ) : message.role === 'user' ? (
                      <MessageBubble isUser={true}>
                        <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.7 }}>
                          {parsedContent.text}
                        </Typography>
                      </MessageBubble>
                    ) : (
                      <Stack spacing={1}>
                        {sentences.map((sentence, idx) => (
                          <MessageBubble key={idx} isUser={false}>
                            <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.7 }}>
                              {sentence}
                            </Typography>
                          </MessageBubble>
                        ))}
                        {parsedContent.links.length > 0 && (
                          <Stack direction="row"
spacing={1}
sx={{ mt: 0.5, flexWrap: 'wrap', gap: 1 }}>
                            {parsedContent.links.map((link, i) => (
                              <ActionLinkButton
                                key={i}
                                size="small"
                                endIcon={<ArrowRightIcon style={{ width: 16, height: 16 }} />}
                                onClick={() => handleLinkClick(link.url)}
                              >
                                {link.label}
                              </ActionLinkButton>
                            ))}
                          </Stack>
                        )}
                      </Stack>
                    )}
                    {message.toolsUsed && message.toolsUsed.length > 0 && (
                      <Stack
                        direction="row"
                        spacing={0.75}
                        sx={{ mt: 1, flexWrap: 'wrap', gap: 0.75 }}
                      >
                        {message.toolsUsed.map((tool, i) => (
                          <ToolChip
                            key={i}
                            icon={<CheckCircleIcon style={{ width: 12, height: 12 }} />}
                            label={tool.replace(/_/g, ' ')}
                            size="small"
                          />
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Stack>
              </Fade>
            );
          })}

          <div ref={messagesEndRef} />
        </Stack>
      </Box>

      {/* Input Section - Premium Dark Design */}
      <Box
        sx={{
          px: 3,
          py: 3,
          background: (theme) =>
            theme.palette.mode === 'dark'
              ? 'linear-gradient(180deg, rgba(10,10,11,0) 0%, rgba(10,10,11,1) 100%)'
              : 'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(250,250,250,1) 100%)',
          position: 'relative',
        }}
      >
        {/* Subtle glow effect behind input area */}
        <Box
          sx={{
            position: 'absolute',
            bottom: '40%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 200,
            height: 100,
            background: 'radial-gradient(ellipse, rgba(41,112,255,0.08) 0%, transparent 70%)',
            filter: 'blur(30px)',
            pointerEvents: 'none',
            zIndex: 0
          }}
        />

        {/* Spinning ChatGPT Logo above input - Enterprise Design */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3, position: 'relative', zIndex: 1 }}>
          {/* Outer glow ring */}
          <Box
            sx={{
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 90,
                height: 90,
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(41,112,255,0.12) 0%, transparent 70%)',
                animation: robotAnimating ? `${pulse} 2s ease-in-out` : 'none',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 72,
                height: 72,
                borderRadius: '50%',
                border: '1px solid rgba(41,112,255,0.08)',
              }
            }}
          >
            {/* Inner container with gradient border */}
            <Box
              sx={{
                position: 'relative',
                p: '2px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(41,112,255,0.4) 0%, rgba(41,112,255,0.1) 50%, rgba(41,112,255,0.3) 100%)',
                boxShadow: '0 8px 32px rgba(41,112,255,0.15), 0 0 0 1px rgba(41,112,255,0.05)',
              }}
            >
              {/* Inner circle background */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  background: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'linear-gradient(145deg, #0f1419 0%, #0a0a0b 100%)'
                      : 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                  boxShadow: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'inset 0 1px 0 rgba(255,255,255,0.05), inset 0 -1px 0 rgba(0,0,0,0.2)'
                      : 'inset 0 1px 0 rgba(255,255,255,0.8), inset 0 -1px 0 rgba(0,0,0,0.05)',
                }}
              >
                <ChatGPTIcon size={48} animate={robotAnimating} continuousSpin={!robotAnimating} transparent white />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Alerts section - centered */}
        <Box sx={{ maxWidth: 460, mx: 'auto', position: 'relative', zIndex: 1 }}>
          {!apiKey && !usingEnvKey && (
            <Alert
              severity="warning"
              sx={{
                mb: 2,
                fontSize: '0.8rem',
                borderRadius: 3,
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(251,191,36,0.1)' : 'rgba(251,191,36,0.08)',
                border: '1px solid rgba(251,191,36,0.2)',
                '& .MuiAlert-icon': { fontSize: 18, color: '#FBBF24' }
              }}
            >
              Configure your API key to start chatting
            </Alert>
          )}
          {cooldownSeconds > 0 && (
            <Alert
              severity="info"
              sx={{
                mb: 2,
                fontSize: '0.8rem',
                borderRadius: 3,
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(41,112,255,0.1)' : 'rgba(41,112,255,0.08)',
                border: '1px solid rgba(41,112,255,0.2)',
                '& .MuiAlert-icon': { fontSize: 18, color: '#2970FF' }
              }}
            >
              <Stack direction="row"
alignItems="center"
spacing={1}>
                <CircularProgress size={14}
thickness={5}
sx={{ color: '#2970FF' }} />
                <span>Rate limit cooldown: <strong>{cooldownSeconds}s</strong> remaining</span>
              </Stack>
            </Alert>
          )}
          {/* Voice error alert */}
          {voiceError && (
            <Alert
              severity="warning"
              onClose={() => setVoiceError(null)}
              sx={{
                mb: 2,
                fontSize: '0.8rem',
                borderRadius: 3,
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(251,191,36,0.1)' : 'rgba(251,191,36,0.08)',
                border: '1px solid rgba(251,191,36,0.2)',
                '& .MuiAlert-icon': { fontSize: 18, color: '#FBBF24' }
              }}
            >
              {voiceError}
            </Alert>
          )}
        </Box>

        {/* Voice status indicators - centered */}
        {(isRecording || isProcessing) && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="center"
            sx={{ mb: 2, position: 'relative', zIndex: 1 }}
          >
            {isRecording && (
              <Chip
                size="small"
                icon={<MicrophoneIcon sx={{ fontSize: 14, color: '#EF4444' }} />}
                label={`Recording... ${Math.round(audioLevel * 100)}%`}
                sx={{
                  height: 28,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  bgcolor: 'rgba(239,68,68,0.15)',
                  color: '#EF4444',
                  border: '1px solid rgba(239,68,68,0.3)',
                  animation: `${pulse} 1.5s ease-in-out infinite`
                }}
              />
            )}
            {isProcessing && (
              <Chip
                size="small"
                icon={<CircularProgress size={12}
sx={{ color: '#2970FF' }} />}
                label="Transcribing..."
                sx={{
                  height: 28,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  bgcolor: 'rgba(41,112,255,0.15)',
                  color: '#2970FF',
                  border: '1px solid rgba(41,112,255,0.3)'
                }}
              />
            )}
          </Stack>
        )}

        {/* Audio level indicator - centered */}
        {isRecording && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, position: 'relative', zIndex: 1 }}>
            <LinearProgress
              variant="determinate"
              value={audioLevel * 100}
              sx={{
                width: '50%',
                height: 3,
                borderRadius: 2,
                bgcolor: 'rgba(239,68,68,0.15)',
                '& .MuiLinearProgress-bar': {
                  bgcolor: '#EF4444',
                  borderRadius: 2,
                  boxShadow: '0 0 10px rgba(239,68,68,0.5)'
                }
              }}
            />
          </Box>
        )}

        {/* Input field - premium glass design */}
        <Box sx={{ maxWidth: 480, mx: 'auto', position: 'relative', zIndex: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={
              isRecording
                ? 'Listening... click mic to stop'
                : isProcessing
                  ? 'Transcribing audio...'
                  : cooldownSeconds > 0
                    ? `Please wait ${cooldownSeconds}s...`
                    : apiKey || usingEnvKey
                      ? 'Ask anything...'
                      : 'Configure API key first...'
            }
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading || isProcessing || (!apiKey && !usingEnvKey) || cooldownSeconds > 0}
            multiline
            maxRows={4}
            InputProps={{
              startAdornment: isVoiceSupported ? (
                <InputAdornment position="start">
                  <Tooltip title={isRecording ? 'Stop recording' : isProcessing ? 'Processing...' : 'Start voice input'}>
                    <span>
                      <IconButton
                        onClick={toggleRecording}
                        disabled={isLoading || isProcessing || (!apiKey && !usingEnvKey) || cooldownSeconds > 0}
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '10px',
                          backgroundColor: isRecording ? '#EF4444' : 'rgba(41,112,255,0.1)',
                          color: isRecording ? '#fff' : '#2970FF',
                          border: isRecording ? 'none' : '1px solid rgba(41,112,255,0.2)',
                          animation: isRecording ? `${micPulse} 1.5s ease-in-out infinite` : 'none',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: isRecording ? '#DC2626' : 'rgba(41,112,255,0.2)',
                            transform: 'scale(1.05)',
                            boxShadow: isRecording ? '0 0 20px rgba(239,68,68,0.4)' : '0 0 15px rgba(41,112,255,0.3)'
                          },
                          '&.Mui-disabled': {
                            color: 'text.disabled',
                            backgroundColor: 'transparent',
                            border: 'none'
                          }
                        }}
                      >
                        {isRecording ? (
                          <StopIcon sx={{ fontSize: 18 }} />
                        ) : (
                          <MicrophoneIcon sx={{ fontSize: 18 }} />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                </InputAdornment>
              ) : undefined,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleSend}
                    disabled={!input.trim() || isLoading || isProcessing || (!apiKey && !usingEnvKey) || cooldownSeconds > 0}
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: '10px',
                      background: input.trim()
                        ? 'linear-gradient(135deg, #2970FF 0%, #1e5fd9 100%)'
                        : 'rgba(41,112,255,0.1)',
                      color: input.trim() ? '#fff' : 'rgba(41,112,255,0.5)',
                      border: input.trim() ? 'none' : '1px solid rgba(41,112,255,0.15)',
                      transition: 'all 0.2s ease',
                      boxShadow: input.trim() ? '0 4px 15px rgba(41,112,255,0.35)' : 'none',
                      '&:hover': {
                        background: input.trim()
                          ? 'linear-gradient(135deg, #1e5fd9 0%, #1a4fc4 100%)'
                          : 'rgba(41,112,255,0.15)',
                        transform: input.trim() ? 'scale(1.05)' : 'none',
                        boxShadow: input.trim() ? '0 6px 20px rgba(41,112,255,0.45)' : 'none'
                      },
                      '&.Mui-disabled': {
                        color: 'text.disabled',
                        backgroundColor: 'transparent',
                        border: 'none',
                        boxShadow: 'none'
                      }
                    }}
                  >
                    <ChevronRightIcon style={{ width: 18, height: 18 }} />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                borderRadius: '14px',
                py: 1.25,
                minHeight: 54,
                alignItems: 'center',
                bgcolor: isRecording
                  ? 'rgba(239,68,68,0.08)'
                  : (theme) => theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.03)'
                    : 'rgba(0,0,0,0.02)',
                border: isRecording
                  ? '1px solid rgba(239,68,68,0.4)'
                  : (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
                backdropFilter: 'blur(10px)',
                '& fieldset': { border: 'none' },
                transition: 'all 0.25s ease',
                '& .MuiInputAdornment-positionStart': {
                  marginRight: '8px',
                  marginTop: '0 !important',
                  alignSelf: 'center'
                },
                '& .MuiInputAdornment-positionEnd': {
                  marginTop: '0 !important',
                  alignSelf: 'center'
                },
                '&:hover': {
                  bgcolor: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                  borderColor: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgba(41,112,255,0.3)' : 'rgba(41,112,255,0.25)',
                  boxShadow: '0 0 20px rgba(41,112,255,0.08)'
                },
                '&.Mui-focused': {
                  bgcolor: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                  borderColor: 'rgba(41,112,255,0.5)',
                  boxShadow: '0 0 0 3px rgba(41,112,255,0.1), 0 0 30px rgba(41,112,255,0.1)'
                },
                pr: 1,
                pl: isVoiceSupported ? 1 : 2
              }
            }}
            sx={{
              '& .MuiInputBase-input': {
                fontSize: '0.9rem',
                color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.85)',
                '&::placeholder': {
                  opacity: 0.5,
                  color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)'
                }
              }
            }}
          />
        </Box>

      </Box>

      {/* Loading Overlay for Report Generation */}
      <Fade in={isGeneratingReport}>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: (theme) =>
              theme.palette.mode === 'dark'
                ? 'rgba(10, 10, 11, 0.95)'
                : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(8px)',
            zIndex: 9999,
            gap: 3,
          }}
        >
          {/* Animated ChatGPT Logo */}
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* Outer spinning ring */}
            <Box
              sx={{
                position: 'absolute',
                width: 100,
                height: 100,
                borderRadius: '50%',
                border: '3px solid transparent',
                borderTopColor: '#10B981',
                borderRightColor: '#2970FF',
                animation: 'spin 1.5s linear infinite',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            />
            {/* Inner pulsing glow */}
            <Box
              sx={{
                position: 'absolute',
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(41,112,255,0.2) 0%, transparent 70%)',
                animation: 'pulse 2s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': { transform: 'scale(1)', opacity: 0.5 },
                  '50%': { transform: 'scale(1.1)', opacity: 1 },
                },
              }}
            />
            {/* ChatGPT Logo */}
            <ChatGPTIcon size={64} transparent white />
          </Box>

          {/* Loading Text */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              sx={{
                fontSize: '1.1rem',
                fontWeight: 600,
                color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#0a0a0b',
                mb: 1,
              }}
            >
              Generating Quarterly Analytics
            </Typography>
            <Typography
              sx={{
                fontSize: '0.85rem',
                color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
              }}
            >
              Compiling revenue, sales, and performance data...
            </Typography>
          </Box>

          {/* Progress indicator */}
          <Box sx={{ width: 200 }}>
            <LinearProgress
              sx={{
                height: 4,
                borderRadius: 2,
                backgroundColor: (theme) =>
                  theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 2,
                  background: 'linear-gradient(90deg, #10B981 0%, #2970FF 50%, #8B5CF6 100%)',
                  backgroundSize: '200% 100%',
                  animation: 'shimmer 1.5s ease-in-out infinite',
                  '@keyframes shimmer': {
                    '0%': { backgroundPosition: '200% 0' },
                    '100%': { backgroundPosition: '-200% 0' },
                  },
                },
              }}
            />
          </Box>
        </Box>
      </Fade>
      </StyledDrawer>
    </>
  );
};
